import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Star, MapPin, Filter, X, SlidersHorizontal } from 'lucide-react';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import RecentlyViewed from '../components/RecentlyViewed';
import FavoriteButton from '../components/FavoriteButton';
import { ArtistBadges, isTopArtist } from '../components/VerificationBadges';
import { FeaturedArtists } from '../components/FeaturedArtists';

const GENRE_OPTIONS = [
  'riddim', 'dubstep', 'deepdub', 'experimental',
  'pop', 'rock', 'hip-hop', 'r&b', 'electronic', 'jazz', 'classical',
  'country', 'reggae', 'blues', 'folk', 'metal', 'punk', 'soul',
  'disco', 'house', 'techno', 'ambient', 'dnb'
];

export default function BrowseArtists() {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [me, setMe] = useState(null);
  useEffect(() => {
    apiClient.get('/auth/me').then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [minRating, setMinRating] = useState('');
  const [isLocal, setIsLocal] = useState(null);
  const [localArea, setLocalArea] = useState('');
  const [availableDate, setAvailableDate] = useState('');

  useEffect(() => {
    fetchArtists();
    // eslint-disable-next-line
  }, [selectedGenres]);

  const fetchArtists = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (search) params.append('search', search);
      if (selectedGenres.length > 0) params.append('genres', selectedGenres.join(','));
      if (minRating) params.append('min_rating', minRating);
      if (isLocal !== null) params.append('is_local', isLocal);
      if (localArea) params.append('local_area', localArea);
      if (availableDate) params.append('available_date', availableDate);
      
      const queryString = params.toString();
      const response = await apiClient.get(`/artists${queryString ? '?' + queryString : ''}`);
      setArtists(response.data);
    } catch (error) {
      console.error('Error fetching artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchArtists();
  };

  const clearFilters = () => {
    setSelectedGenres([]);
    setMinRating('');
    setIsLocal(null);
    setLocalArea('');
    setAvailableDate('');
    setSearch('');
  };

  const toggleGenre = (genre) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const activeFiltersCount = [
    selectedGenres.length > 0,
    minRating,
    isLocal !== null,
    localArea,
    availableDate
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="mb-8 pt-8">
          <div className="mb-6">
            <h1 className="text-5xl font-bold gradient-text mb-2">Discover Artists</h1>
            <p className="text-white/60 text-lg">Find the perfect performer for your event</p>
          </div>

          <FeaturedArtists user={me} />

          {/* Search Bar */}
          <div className="glass-default rounded-2xl p-4 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search by genre, style, or keyword..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-white/5 border-white/10 focus:border-violet-500 focus:ring-violet-500/20 text-white placeholder:text-white/30"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={`border-white/20 hover:bg-white/10 ${showFilters ? 'bg-white/10' : ''}`}
              >
                <SlidersHorizontal size={18} className="mr-2" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-2 w-5 h-5 rounded-full bg-violet-500 text-xs flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
              <Button
                data-testid="search-button"
                onClick={handleSearch}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500"
              >
                Search
              </Button>
            </div>
          </div>

          {/* Genre Spotlight — quick chips */}
          <div className="flex flex-wrap gap-2 mt-4" data-testid="genre-spotlight">
            {[
              { label: '🔊 Riddim', value: 'riddim' },
              { label: '🎚️ Dubstep', value: 'dubstep' },
              { label: '🌊 DeepDub', value: 'deepdub' },
              { label: '🧪 Experimental', value: 'experimental' },
              { label: '🏠 House', value: 'house' },
              { label: '🎤 Hip-Hop', value: 'hip-hop' },
              { label: '⚡ EDM', value: 'electronic' },
              { label: '🎛️ Techno', value: 'techno' },
              { label: '🥁 DnB', value: 'dnb' },
            ].map(({ label, value }) => {
              const active = selectedGenres.includes(value);
              return (
                <button
                  key={value}
                  type="button"
                  data-testid={`genre-chip-${value}`}
                  onClick={() => toggleGenre(value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    active
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 border-violet-400 text-white shadow-lg shadow-violet-500/30 scale-105'
                      : 'bg-white/5 border-white/15 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="glass-default rounded-2xl p-6 mt-4 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Filter size={20} className="text-violet-400" />
                  Advanced Filters
                </h3>
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-400 hover:text-red-300">
                    <X size={16} className="mr-1" />
                    Clear All
                  </Button>
                )}
              </div>

              {/* Genres */}
              <div>
                <label className="text-sm text-white/60 mb-2 block">Genres</label>
                <div className="flex flex-wrap gap-2">
                  {GENRE_OPTIONS.map(genre => (
                    <button
                      key={genre}
                      onClick={() => toggleGenre(genre)}
                      className={`px-3 py-1 rounded-full text-sm transition-all ${
                        selectedGenres.includes(genre)
                          ? 'bg-violet-500 text-white'
                          : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rating and Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Min Rating</label>
                  <Input
                    type="number"
                    placeholder="0"
                    min="0"
                    max="5"
                    step="0.5"
                    value={minRating}
                    onChange={(e) => setMinRating(e.target.value)}
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-2 block">City / Area</label>
                  <Input
                    type="text"
                    placeholder="e.g., Los Angeles, Miami"
                    value={localArea}
                    onChange={(e) => setLocalArea(e.target.value)}
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Available On</label>
                  <Input
                    type="date"
                    value={availableDate}
                    onChange={(e) => setAvailableDate(e.target.value)}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              </div>

              {/* Location Type */}
              <div>
                <label className="text-sm text-white/60 mb-2 block">Location</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsLocal(isLocal === true ? null : true)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      isLocal === true
                        ? 'bg-violet-500 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    <MapPin size={16} className="inline mr-1" />
                    Local Only
                  </button>
                  <button
                    onClick={() => setIsLocal(isLocal === false ? null : false)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      isLocal === false
                        ? 'bg-violet-500 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    Touring Artists
                  </button>
                </div>
              </div>

              <Button onClick={handleSearch} className="w-full bg-violet-600 hover:bg-violet-500">
                Apply Filters
              </Button>
            </div>
          )}
        </div>

        {/* Recently Viewed */}
        <RecentlyViewed />

        {/* Results Count */}
        {!loading && (
          <p className="text-white/60 mb-4">
            Found {artists.length} artist{artists.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Artists Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : artists.length === 0 ? (
          <div className="glass-default rounded-2xl p-12 text-center">
            <p className="text-xl text-white/60 mb-4">No artists found matching your criteria.</p>
            <Button variant="outline" onClick={clearFilters} className="border-white/20">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {artists.map((artist) => (
              <Card
                key={artist.artist_id}
                data-testid={`artist-card-${artist.artist_id}`}
                className="glass-default border-white/10 hover-lift cursor-pointer overflow-hidden group"
                onClick={() => navigate(`/artist/${artist.artist_id}`)}
              >
                <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20">
                  {artist.media_urls && artist.media_urls[0] ? (
                    <img
                      src={artist.media_urls[0]}
                      alt="Artist"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Star className="w-16 h-16 text-violet-400/50" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <FavoriteButton artistId={artist.artist_id} size="sm" />
                  </div>
                  {isTopArtist(artist) && (
                    <div
                      className="absolute bottom-3 left-3 top-artist-ribbon rounded-full px-3 py-1 flex items-center gap-1 shadow-lg shadow-amber-500/30"
                      data-testid={`top-artist-badge-${artist.artist_id}`}
                    >
                      <span className="text-xs font-extrabold text-amber-950 tracking-wide">🏆 TOP ARTIST</span>
                    </div>
                  )}
                  {artist.rating > 0 && (
                    <div className="absolute top-3 right-3 glass-heavy rounded-full px-3 py-1 flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-semibold">{(artist.rating || 0).toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-xl truncate">{artist.stage_name || artist.name || artist.bio.split(' ').slice(0, 3).join(' ')}</h3>
                    <ArtistBadges artist={artist} size="xs" />
                  </div>
                  <p className="text-sm text-white/60 line-clamp-2">{artist.bio}</p>
                  <div className="flex flex-wrap gap-2">
                    {artist.genres.slice(0, 2).map((genre, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 rounded-full bg-violet-500/20 text-violet-300">
                        {genre}
                      </span>
                    ))}
                  </div>
                </CardContent>
                <CardFooter className="p-4 pt-0 flex items-center justify-between border-t border-white/10">
                  <div className="flex items-center gap-2 text-sm text-white/60">
                    <MapPin size={16} />
                    <span>{artist.is_local ? 'Local' : 'Touring'}</span>
                  </div>
                  <span className="text-sm font-semibold text-violet-300 group-hover:text-violet-100 transition-colors" data-testid="artist-card-view-profile">
                    View Profile <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
